import {RefObject, useCallback, useEffect, useRef} from 'react';
import {getTypedObjectKeys} from '../shared/utils/getTypedObjectKeys';
import {useWrapRef} from '../useWrapRef.hook';

interface Params {
  stickyRef: RefObject<HTMLElement>;
  stickyMargin?: number;
  // default body element
  rootRef?: RefObject<HTMLElement>;
  defaultRootRef?: 'window' | 'body';
  disabled?: boolean;
  getStickyTop?: (
    stickyInfo: StickyInfoType
  ) => number | undefined | null | void;
}

type StickyInfoType = {
  stickyTop: number;
  stickyHeight: number;
  rootHeight: number;
};

type OriginalStyleType = {
  top: string | null;
  position: string | null;
};

export const useScrollStickyToBottom = ({
  stickyRef,
  stickyMargin = 0,
  rootRef,
  defaultRootRef = 'window',
  disabled,
  getStickyTop,
}: Params) => {
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const originalStyleRef = useRef<OriginalStyleType>({
    top: null,
    position: null,
  });
  const stickyInfoRef = useRef<StickyInfoType>({
    stickyTop: 0,
    stickyHeight: 0,
    rootHeight: 0,
  });

  const getStickyTopRef = useWrapRef(getStickyTop);

  // 스티키 정보를 업데이트 하기 위한 돔 정보를 구합니다.
  const getElementMap = useCallback(() => {
    const stickyEl = stickyRef.current!;
    const rootRefEl = rootRef?.current;
    const rootEl = (
      rootRefEl
        ? rootRefEl
        : defaultRootRef === 'window'
          ? window
          : document.body
    )!;
    return {stickyEl, rootEl};
  }, [stickyRef, rootRef, defaultRootRef]);

  // 스티키 정보를 업데이트 합니다.
  const updateStickyInfo = useCallback(() => {
    const {stickyEl, rootEl} = getElementMap();
    if (!stickyEl || !rootEl) return;

    getTypedObjectKeys(originalStyleRef.current).forEach(key => {
      // 맨 처음 값을 주입하기 위해 null 로 체크합니다.
      if (originalStyleRef.current[key] === null) {
        // 아무 스타일이 없는 경우 빈문자열을 반환합니다.
        // null 대신 빈문자열을 주입하기 때문에 다음부턴 위에 if문 조건에 해당하지 않습니다.
        const styleValue = stickyEl.style.getPropertyValue(key);
        originalStyleRef.current[key] = styleValue;
      }
    });

    const isWindow = 'innerHeight' in rootEl;
    const stickyRect = stickyEl.getBoundingClientRect();

    const stickyHeight = stickyRect.height;
    const rootHeight = isWindow
      ? window.visualViewport?.height || window.innerHeight
      : rootEl.getBoundingClientRect().height;

    // 최신 데이터를 전달하기 위해 getStickyTop 함수 호출 전에 스티키 높이와 루트 높이를 업데이트 합니다.
    stickyInfoRef.current.stickyHeight = stickyHeight;
    stickyInfoRef.current.rootHeight = rootHeight;
    stickyInfoRef.current.stickyTop =
      getStickyTopRef.current?.(stickyInfoRef.current) ??
      rootHeight - stickyHeight - stickyMargin;
  }, [stickyMargin, getElementMap, getStickyTopRef]);

  useEffect(() => {
    const {stickyEl} = getElementMap();
    if (disabled || !stickyEl) return;

    const handleResize = () => {
      updateStickyInfo();

      const {stickyTop} = stickyInfoRef.current;
      stickyEl.style.position = 'sticky';
      stickyEl.style.top = `${stickyTop}px`;
    };

    const ob = new ResizeObserver(handleResize);
    resizeObserverRef.current = ob;
    ob.observe(stickyEl);

    handleResize();
    // 모바일 기기에서 resize 될 때 이벤트가 즉시 동작하지 않는 현상이 있는데,
    // visualViewport API 를 사용하면 문제가 해결 됩니다.
    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleResize);
    } else {
      window.addEventListener('resize', handleResize);
    }
    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleResize);
      } else {
        window.removeEventListener('resize', handleResize);
      }
      resizeObserverRef.current?.disconnect();
      resizeObserverRef.current = null;

      getTypedObjectKeys(originalStyleRef.current).forEach(key => {
        // original 스타일 값이 있으면 원래대로 되돌리고 없다면 제거
        if (originalStyleRef.current[key]) {
          stickyEl.style[key] = originalStyleRef.current[key];
        } else {
          stickyEl.style.removeProperty(key);
        }
        originalStyleRef.current[key] = null;
      });
    };
  }, [disabled, getElementMap, updateStickyInfo]);
};
